/**
 * MIRROR of `server/helpers/hireCost.js` — read the reasoning there.
 *
 * 🔑 **A DISPLAY CONVENIENCE, and NEVER a gate.** The server recomputes
 * `order_items.line_total` on every write, so nothing here decides what is
 * stored. This exists so the order form's "Line Total" and the Hires Gantt's
 * drag preview show the buyer the number the server will actually arrive at,
 * instead of a different one.
 *
 * The two files are held byte-identical (as CODE — the docblocks deliberately
 * differ) by `server/tests/regressions/hire-cost-client-parity.test.js`. If you
 * change one, change the other; the server is the authority.
 *
 * Convention, restated so a reader here does not have to cross the package
 * boundary to learn it: days are **INCLUSIVE** of the end date with a minimum
 * of one (1 Sep → 22 Sep is 22 days), `hire_to` is the **LAST HIRED DAY**, a
 * same-day hire is 1 day, and a hire missing either date has an UNKNOWN
 * duration whose cost is 0 — never one day's rate.
 *
 * ⚠️ **THAT REVERSED AN EARLIER RULING BY THE SAME OWNER ON 2026-08-27** — it
 * was EXCLUSIVE, and `hire_to` was the RETURN date (*"you paid for the day the
 * kit left the yard"*, 2026-08-11). He changed it knowingly: *"Some items need
 * to be returned on the last day, some are returned next day, so its easy if
 * the dates and the gantt chart etc all show the actual paid hired days."*
 * **Do not "restore" the exclusive count believing this was a regression** —
 * both quotes and both dates are recorded in the server copy.
 * And since 2026-08-20, `quantity` multiplies the line on ALL THREE rate types
 * — see `hireLineQuantity` below.
 *
 * 🪤 **A CALLER HERE MUST PASS `quantity` IN, and one of them nearly did not.**
 * `components/OrderForm.js#getLineTotal` does not hand this module the item: it
 * builds a SYNTHETIC literal to map the form's single `hire_rate` onto the three
 * rate columns. A field the literal omits is a field this module cannot see, so
 * omitting `quantity` there made the form preview 1× while the server stored 3×
 * — the same form-says-£50 / Gantt-says-£1,050 split this mirror exists to
 * prevent, wearing a fresh coat. Pinned by
 * `client/src/components/OrderForm.hireTotal.test.js`.
 */

// 🔑 DELIBERATELY ABOVE `const MS_PER_DAY` — that declaration is where
// `server/tests/regressions/hire-cost-client-parity.test.js` starts comparing
// this file against `server/helpers/hireCost.js`, and the two modules import
// the primitive with different syntax (ESM here, CommonJS there). Keeping both
// imports OUTSIDE the compared span is what lets the four `roundMoney(
// toHireNumber(…))` CALL SITES inside the span stay textually identical, which
// is the property that parity test exists to hold. 🔀 Until T7 the property was
// about the two `round2` BODIES; `round2` is gone and the calls are the mirror
// now, but the reason the import must stay up here is unchanged.
import { roundMoney } from './money';

const MS_PER_DAY = 24 * 60 * 60 * 1000;

const HIRE_RATE_TYPES = ['daily', 'weekly', 'total'];

export function toHireNumber(value) {
  const n = typeof value === 'number' ? value : parseFloat(value);
  return Number.isFinite(n) ? n : 0;
}

// 🔻 `round2` WAS HERE AND IS DELETED (money.js T7, 2026-09-08), in the SAME
// commit as the server's. It was never optional to move both:
// `server/tests/regressions/hire-cost-client-parity.test.js` compares these two
// files as SOURCE TEXT over the span `const MS_PER_DAY` … close of
// `orderLineTotal`, and both `round2` definitions sat inside it, so deleting one
// side alone turns a MONEY mirror red.
//
// 🪤 LINE COMMENTS, NOT A `/** … */` BLOCK, AND THAT IS DELIBERATE (T7 review,
// M1). A leading JSDoc block binds to the NEXT declaration across a blank line,
// so as a docblock this tombstone became `parseHireDate`'s documentation in
// every IDE hover. Same form as the import note above.
//
// 🔑 THE SPLIT SURVIVES THE FUNCTION, and it is the rule to keep: the four call
// sites below spell `roundMoney(toHireNumber(x))` and never `roundMoney(x)`.
// `toHireNumber` ('2kg' → 2, garbage → 0) is a HIRE rule and stays here; the
// rounding rule lives once, in the money primitive. Without the coercion
// `roundMoney` answers `null` for `null` and for `'2kg'`, and the form's Line
// Total would blank out on a part-typed rate.
//
// 🔴 TWO DECLARED DIVERGENCES from the inline rule this replaced, and the second
// arrived with T7 itself. Both answer `null` where the older code answered a
// number; the full reasoning is at `server/helpers/hireCost.js` and nothing on
// this side may diverge from it.
//   (1) T1 — an overflowing x100 product answers `null` rather than `Infinity`.
//   (2) T7 — an overflowing rate x quantity PRODUCT answers `null` where it
//       answered `0`. `round2(x)` was `roundMoney(toHireNumber(x))`, so the
//       PRODUCT was coerced as well as the rate; `toHireNumber(Infinity)` is `0`,
//       so a rate near 1e308 at quantity 2 used to preview a confident £0.00.
//       🔑 The new answer is the right one — do NOT restore the double coercion.
//       It was silent when it changed (every fixture drives quantity 1, where the
//       outer coercion was a no-op) and is pinned by
//       `server/tests/regressions/money-hire-delegation.test.js` §5.

export function parseHireDate(value) {
  if (value == null) return null;
  let text;
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    text = `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, '0')}-${String(value.getDate()).padStart(2, '0')}`;
  } else {
    text = String(value).trim();
  }
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(text);
  if (!m) return null;
  const ms = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isFinite(ms) ? ms : null;
}

/**
 * The plain CALENDAR GAP between two dates, EXCLUSIVE of the second.
 *
 * 🔑 NOT a hire duration. Two different KINDS of interval are measured over
 * hire dates and only one is a hire: a HIRE SPAN (`hire_from` → `hire_to`) has
 * two held days as endpoints and is INCLUSIVE; a GAP BETWEEN TWO END DATES
 * (planned end → actual return, the overrun) is EXCLUSIVE, because returning
 * kit ON its planned last day is zero overrun. Full reasoning in the server
 * copy. Do not collapse them.
 */
export function calendarDayGap(from, to) {
  const a = parseHireDate(from);
  const b = parseHireDate(to);
  if (a === null || b === null) return null;
  return Math.round((b - a) / MS_PER_DAY);
}

/**
 * The BILLABLE SPAN of a hire, INCLUSIVE of `hire_to` — the last hired day
 * (product-owner ruling 2026-08-27, reversing his 2026-08-11 return-date
 * ruling; both quotes are recorded in the server copy). `null` is UNKNOWN,
 * never zero.
 */
export function hireDaysBetween(from, to) {
  const raw = calendarDayGap(from, to);
  return raw === null ? null : raw + 1;
}

export function billableHireDays(from, to) {
  const raw = hireDaysBetween(from, to);
  return raw === null ? null : Math.max(raw, 1);
}

export function normaliseHireRateType(value) {
  const type = value == null ? '' : String(value).trim().toLowerCase();
  return HIRE_RATE_TYPES.includes(type) ? type : 'total';
}

/**
 * How many of this item are on the line. THE one place `quantity` is turned
 * into a multiplier, for hire and non-hire lines alike.
 *
 * 🔴 Product-owner RULING, 2026-08-20 — quantity multiplies ALL THREE rate
 * types, **including `total`**: the rates are PER-ITEM and `quantity` is the
 * per-line multiplier, so `total` is the lump sum for ONE item's hire period.
 * A ruling, not an inference; the full argument is in the server copy.
 *
 * 🚨 **ZERO MEANS ONE.** `0` is not a legitimate quantity (the form opens every
 * item on 1, and the non-hire branch has read `quantity || 1` since before this
 * module existed), so it is the absence marker here — unlike lead time, where
 * `0` days genuinely means "same day".
 *
 * 🪤 Arrives as a STRING from the DOM and as `null`/absent from the database,
 * and the string `'0'` is TRUTHY — hence coerce first, then test `> 0`.
 */
export function hireLineQuantity(item) {
  const n = toHireNumber(item && item.quantity);
  return n > 0 ? n : 1;
}

export function hireCostForDays(item, days) {
  const type = normaliseHireRateType(item && item.hire_rate_type);
  // 🔑 The multiplier is applied HERE, the deepest common point, and that is
  // load-bearing: `routes/hires.js#computeHireCosts` calls this function
  // DIRECTLY (expected / current / overrun / final), bypassing `hireLineCost`.
  // Applying it one level up would leave those four at 1× while the stored
  // `line_total` went to 3× — the £50-vs-£1,050 cross-surface disagreement
  // this module was created to close. Multiply BEFORE `roundMoney`, so money is
  // rounded once for the line rather than once per unit.
  const qty = hireLineQuantity(item);
  if (type === 'total') return roundMoney(toHireNumber(item && item.total_hire_rate) * qty);
  if (days == null || !Number.isFinite(Number(days))) return 0;
  const billable = Math.max(Number(days), 1);
  if (type === 'daily') return roundMoney(toHireNumber(item && item.daily_rate) * billable * qty);
  return roundMoney(toHireNumber(item && item.weekly_rate) * Math.ceil(billable / 7) * qty);
}

export function hireLineCost(item) {
  if (!item) return 0;
  return hireCostForDays(item, hireDaysBetween(item.hire_from, item.hire_to));
}

/**
 * Where a hire's span sits against the WEEKS it is actually billed for.
 * `null` when the duration is unknown. Full reasoning in the server copy.
 *
 * 🔑 A weekly hire bills whole weeks (`Math.ceil`), so the days between the end
 * date and the end of the paid week are ALREADY PAID FOR — an OFFER, not a
 * warning. 🚧 Added 2026-08-27 for a queued follow-on; no UI is built on it yet.
 */
export function hireWeekBoundary(from, to) {
  const days = billableHireDays(from, to);
  if (days === null) return null;
  const billedWeeks = Math.ceil(days / 7);
  const paidDays = billedWeeks * 7;
  const start = parseHireDate(from);
  let fullWeekEndDate = null;
  if (start !== null) {
    const end = new Date(start + (paidDays - 1) * MS_PER_DAY);
    const y = end.getUTCFullYear();
    const m = String(end.getUTCMonth() + 1).padStart(2, '0');
    const d = String(end.getUTCDate()).padStart(2, '0');
    fullWeekEndDate = `${y}-${m}-${d}`;
  }
  return {
    days,
    billedWeeks,
    paidDays,
    freeDays: paidDays - days,
    isWholeWeeks: days === paidDays,
    fullWeekEndDate,
  };
}

/**
 * Signed £ delta if the hire actually ran `hire_from` → `effectiveEnd` instead
 * of the planned span. Full reasoning in the server copy. Whole-span
 * arithmetic on purpose — pricing the overrun gap in isolation double-charges
 * a weekly line still inside its already-paid week. Positive = overdue
 * exposure, negative = early-return saving. `null` when either span is
 * unknowable — never 0-as-money. NEVER persisted, NEVER applied automatically.
 */
export function hireOverrunDelta(item, effectiveEnd) {
  if (!item || !effectiveEnd) return null;
  const planned = hireDaysBetween(item.hire_from, item.hire_to);
  const actual = hireDaysBetween(item.hire_from, effectiveEnd);
  if (planned === null || actual === null) return null;
  return roundMoney(toHireNumber(hireLineCost({ ...item, hire_to: effectiveEnd }) - hireLineCost(item)));
}

export function orderLineTotal(item) {
  if (!item) return 0;
  if (item.is_hire) return hireLineCost(item);
  // ⚠️ NOT rounded — the old expression was not, and rounding would move
  // existing non-hire rows.
  //
  // 🔻 CORRECTED 2026-08-20. This said `hireLineQuantity` is exactly
  // `quantity || 1` "for every value this column holds", and that is FALSE.
  // It holds only for values that reach here through the API's
  // `z.number().positive()` gate on `quantity`. Measured against a RAW row it
  // diverges three ways: the STRING `'0'` → `||` gives 0, this gives 1; `-3` →
  // `||` gives −150, this gives 50; `'2kg'` → `||` gives NaN, this gives 100,
  // because `toHireNumber` parseFloats it to **2**, not to 1.
  // 🪤 Sharper on the CLIENT than on the server, because this side is fed by
  // the DOM: an `<input type="number">` yields STRINGS, so `'0'` is the value
  // this function actually sees while a user is mid-edit. Do not re-inline the
  // `||` here on the theory that the two agree.
  // It is still called here rather than re-inlining the `||` so that the two
  // branches can never again disagree about whether quantity counts.
  // Pinned by §7.5 of `regressions/hire-cost.test.js`.
  return hireLineQuantity(item) * (item.unit_price || 0);
}

/**
 * The rate-DISPLAY string for a hire item — `"£500.00/week"`, `"£50.00/day"`,
 * `"£1,050.00 each"`. Added 2026-09-02 for `utils/hireMoneyWarning.js`'s money
 * dialog copy builder.
 *
 * 🔑 **DELIBERATELY OUTSIDE THE SERVER-PARITY MIRROR** — placed after
 * `orderLineTotal`, which is where `hire-cost-client-parity.test.js`'s text
 * extraction stops (it slices from `const MS_PER_DAY` to the end of
 * `orderLineTotal` only). This is display formatting the server never needs;
 * it computes no money and recomputes no `line_total`.
 *
 * 🪤 **CONSOLIDATES TWO PRIVATE COPIES THAT HAD DRIFTED, found while adding a
 * third caller.** `hires/GanttView.js` and `hires/HireCard.js` each carried
 * their own `getRateDisplay(item, fmt)`, and they disagreed on the `total`
 * branch: `HireCard.js` says `"£X each"` (product-owner ruling, 2026-08-20 —
 * quoted in full at that file — because all three rate columns are PER-ITEM
 * and a quantity > 1 line's "total" label was asserting it WAS the line's
 * cost while the figure beside it disagreed); `GanttView.js` still says
 * `"£X total"`, unfixed since that ruling landed. This export takes
 * `HireCard.js`'s wording — the one that matches the live product decision —
 * and is used only by the new money-summary builder; `GanttView.js`'s and
 * `HireCard.js`'s own private copies are UNCHANGED by this addition (fixing
 * their drift is a separate, undeclared piece of work — flagged, not done
 * here, to avoid smuggling an unrelated behaviour change into this feature).
 *
 * Takes the formatter as a parameter, not `useVatMode()` — this is a
 * module-level function and the hook is only reachable inside a component.
 */
export function hireRateDisplay(item, fmt) {
  const type = normaliseHireRateType(item && item.hire_rate_type);
  if (type === 'daily') return `${fmt(item.daily_rate)}/day`;
  if (type === 'weekly') return `${fmt(item.weekly_rate)}/week`;
  return `${fmt(item && item.total_hire_rate)} each`;
}

/**
 * The hire's length in the unit the line is BILLED in — `"4 days"` for a
 * daily or total-rate hire, `"3 wks"` for a weekly one. Moved out of
 * `components/OrderForm.js` 2026-09-29 (prototype `7d48785a`, product owner:
 * *"next to Qty:1 could we add something like 6wks or 3 days so the Quantity
 * and total price make sense"*).
 *
 * Built on `billableHireDays` and the SAME `Math.ceil(days / 7)` weekly rule
 * `hireCostForDays` uses below. 🔑 **DELIBERATELY placed AFTER `orderLineTotal`,
 * like `hireRateDisplay` above** — `hire-cost-client-parity.test.js` extracts
 * only `const MS_PER_DAY` … the close of `orderLineTotal` as source text, and
 * a hire's DISPLAYED length is not something the server ever needs to agree
 * on, so it must not be pulled inside that mirror. That means the
 * `Math.ceil(days / 7)` below is a second, textual copy of the one inside
 * `hireCostForDays`'s weekly branch rather than a shared internal — a real
 * drift risk a structural share would close but can't be used here without
 * widening the mirror. Closed BEHAVIOURALLY instead: `hireCost.test.js` pins
 * this function's week count against `hireCostForDays`' own for every day
 * count 1..30, so the two cannot silently disagree.
 *
 * `null` when either hire date is missing — an unknown duration has no
 * length to show, the same "`null` is UNKNOWN, never zero" rule as
 * `hireDaysBetween` above.
 */
export function hireBilledLength(item) {
  const days = billableHireDays(item && item.hire_from, item && item.hire_to);
  if (days == null) return null;
  if (normaliseHireRateType(item && item.hire_rate_type) === 'weekly') {
    const wks = Math.ceil(days / 7);
    return `${wks} ${wks === 1 ? 'wk' : 'wks'}`;
  }
  return `${days} ${days === 1 ? 'day' : 'days'}`;
}

/**
 * The hire's length as the order form's "Hire Details" header reads it
 * (owner's staging feedback 2026-09-29): `"12 days"` for a daily or total
 * hire; `"3 weeks"` for a weekly hire of whole weeks; and for a weekly hire
 * with a PART week, `"3 weeks & 2 days (charged as 4 weeks)"` — weekly billing
 * rounds UP (`Math.ceil(days / 7)`, the rule inside `hireCostForDays`), and the
 * collapsed line beside Qty already says "× 4 wks", so the header names the
 * real length AND what it is billed as. A weekly hire shorter than a week
 * reads `"3 days (charged as 1 week)"`. Singular "1 day" / "1 week".
 *
 * Days are INCLUSIVE of the end date (`billableHireDays`). `null` when either
 * date is blank — the header then reads plain "Hire Details".
 *
 * Display-only, so it sits OUTSIDE the `hire-cost-client-parity` mirror span,
 * like `hireBilledLength` above; its charged weeks are pinned against
 * `hireCostForDays` for every day count 1..30 (`hireCost.test.js` §12.8).
 */
export function hireDetailsLength(item) {
  const days = billableHireDays(item && item.hire_from, item && item.hire_to);
  if (days == null) return null;
  const dayWord = (n) => `${n} ${n === 1 ? 'day' : 'days'}`;
  const weekWord = (n) => `${n} ${n === 1 ? 'week' : 'weeks'}`;
  if (normaliseHireRateType(item && item.hire_rate_type) !== 'weekly') return dayWord(days);
  const whole = Math.floor(days / 7);
  const rest = days % 7;
  if (rest === 0) return weekWord(whole);
  const actual = whole > 0 ? `${weekWord(whole)} & ${dayWord(rest)}` : dayWord(rest);
  return `${actual} (charged as ${weekWord(Math.ceil(days / 7))})`;
}

export { MS_PER_DAY, HIRE_RATE_TYPES };
